"use client";

import { Dispatch, FC, HTMLAttributes, SetStateAction, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import clsx from "clsx";
import { List, ListItem, Menu, MenuItem, MenuTarget } from "@mantine/core";

import {
  IHeaderMenuItem,
  IHeaderSingleItem,
} from "@/components/layouts/header/DesktopHeader";
import MenuDownloadSection, {
  IMenuDownloadBook,
} from "@/sections/common/MenuDownloadSection";
import { trackEvent } from "@/utils/analytics";

// -------------------------------------------------------------------------------------

export type ISubCategory = {
  heading: string;
  rank: number;
  items: IHeaderSingleItem[];
};

export type ICategory = {
  heading?: string;
  rank?: number;
  categoryLink?: string;
  subCategory?: ISubCategory[];
  downloadBook?: IMenuDownloadBook;
};

export type IHoverMenuState = {
  setHoverMenu: Dispatch<SetStateAction<string>>;
  hoverMenu: string;
};

export type IMegaMenuDropdownProps = HTMLAttributes<HTMLDivElement> &
  IHoverMenuState & {
    data: IHeaderMenuItem | undefined;
  };

const MegaMenuDropdown: FC<IMegaMenuDropdownProps> = (props) => {
  const { data, hoverMenu, setHoverMenu, className, ...other } = props;

  const [activeCategory, setActiveCategory] = useState(0);

  const path = usePathname();

  const midValue = (number: number) => {
    return Math.floor(number / 2);
  };

  const categoryWithSubCategory = data?.items?.filter(
    (item) =>
      (item.subCategory && item.subCategory.length > 0) || item.downloadBook,
  );
  const categoryWithoutSubCategory = data?.items?.filter(
    (item) =>
      (!item.subCategory || item.subCategory.length === 0) &&
      !item.downloadBook,
  );

  const activeSubCategory =
    categoryWithSubCategory?.[activeCategory]?.subCategory;

  return (
    <div
      className={clsx("h-full w-fit", className)}
      onMouseEnter={() => {
        if (data) {
          setHoverMenu(data.label);
          trackEvent("mega_menu_open", { menu_label: data.label });
        }
      }}
      onMouseLeave={() => setHoverMenu("")}
      {...other}
    >
      {data && (
        <Menu
          trigger="click"
          position="bottom"
          offset={0}
          opened={hoverMenu === data.label}
          withinPortal={false}
          // Match MenuDropdown z-index so the mega menu and regular
          // dropdowns stack at the same layer when they overlap.
          zIndex={999}
          onClose={() => setHoverMenu("")}
        >
          <MenuTarget>
            {/* Trigger span IS the visual row — 60px tall, padded.
                Mantine positions the dropdown relative to this span's
                bounding box, so it must fill the full row. */}
            <span
              className={clsx(
                "flex h-full min-h-[60px] cursor-pointer items-center border-b-[3px] border-solid border-transparent px-4 font-bricolage-grotesque text-base font-normal transition-colors duration-200 hover:border-blue-800",

                // active border while dropdown is open (was previously
                // checking data.href which never matched — hoverMenu is
                // set to data.label)
                hoverMenu === data.label && "!border-blue-800",

                // active border when current route is under this menu
                data.options?.borderActive?.(data, path),
              )}
            >
              {data.label}
            </span>
          </MenuTarget>

          <Menu.Dropdown
            className="!left-0 !z-[999] !w-full rounded-t-none shadow-xl"
            styles={{ dropdown: { left: 0, zIndex: 999 } }}
          >
            <div>
              <div className="container py-16 xl:py-20">
                <div className="flex max-h-[81vh] overflow-y-auto">
                  {/* category section */}
                  <div className="w-full max-w-[302px]">
                    <div className="w-full border-r border-gray-85 pl-5 pr-6 xl:pr-8">
                      <List
                        classNames={{ itemLabel: "h-[24px]" }}
                        className="flex flex-col gap-3 *:text-black-900"
                      >
                        {categoryWithSubCategory?.map((item, idx) => (
                          <ListItem
                            key={idx}
                            className={clsx(
                              "my-3 py-3",
                              idx === 0 && "mt-0 pt-0",
                              categoryWithoutSubCategory &&
                                categoryWithoutSubCategory.length === 0 &&
                                categoryWithSubCategory.length === idx + 1 &&
                                "mb-0 pb-0",
                            )}
                          >
                            {item.categoryLink ? (
                              <Link
                                className={clsx(
                                  "cursor-pointer font-bricolage-grotesque text-base font-normal hover:text-secondary-900",
                                  idx === activeCategory &&
                                    "text-secondary-900",
                                )}
                                onMouseEnter={() => setActiveCategory(idx)}
                                href={item.categoryLink}
                              >
                                {item.heading}
                              </Link>
                            ) : (
                              <span
                                className={clsx(
                                  "cursor-pointer font-bricolage-grotesque text-base font-normal hover:text-secondary-900",
                                  idx === activeCategory &&
                                    "text-secondary-900",
                                )}
                                onMouseEnter={() => setActiveCategory(idx)}
                              >
                                {item.heading}
                              </span>
                            )}
                          </ListItem>
                        ))}
                        {categoryWithoutSubCategory?.map((item, idx) => (
                          <MenuItem
                            key={idx}
                            className={clsx(
                              "my-3 w-fit bg-white py-3 pl-0",
                              categoryWithSubCategory?.length === 0
                                ? idx === 0 && "mt-0 pt-0"
                                : idx === 0 && "mt-3 pt-3",
                              categoryWithoutSubCategory.length === idx + 1 &&
                                "mb-0 pb-0",
                            )}
                            component={Link}
                            href={item.categoryLink || ""}
                          >
                            <span
                              className={clsx(
                                "cursor-pointer font-bricolage-grotesque text-base font-normal hover:text-secondary-900",
                                idx +
                                  (categoryWithSubCategory
                                    ? categoryWithSubCategory.length
                                    : 0) ===
                                  activeCategory && "text-secondary-900",
                              )}
                              onClick={() => setActiveCategory(idx)}
                            >
                              {item.heading}
                            </span>
                          </MenuItem>
                        ))}
                      </List>
                    </div>
                  </div>

                  {/* subcategory section */}
                  {((activeSubCategory && activeSubCategory.length > 0) ||
                    categoryWithSubCategory?.[activeCategory]
                      ?.downloadBook) && (
                    <>
                      {/* Subcategory: left section */}
                      {activeSubCategory &&
                        (activeSubCategory.length > 1 ? (
                          <div className="w-full border-r border-yellow-400">
                            {activeSubCategory
                              ?.slice(0, midValue(activeSubCategory.length))
                              .map((item, index) => (
                                <div
                                  key={index}
                                  className={clsx(
                                    "w-full max-w-[461px] px-8 pb-10 xl:px-10",
                                    index !== 0 &&
                                      "border-t border-yellow-400 pt-8",
                                  )}
                                >
                                  <span className="block font-bricolage-grotesque text-base font-normal text-primary-900">
                                    {item.heading}
                                  </span>
                                  <List className="flex flex-col gap-y-2.5 pl-1 pt-5">
                                    {item.items.map((itm, idx) => (
                                      <MenuItem
                                        key={idx}
                                        component={Link}
                                        href={itm.href}
                                        className="w-fit bg-white py-1.5 font-bricolage-grotesque text-base font-light text-black-900 hover:text-secondary-900"
                                      >
                                        {itm.label}
                                      </MenuItem>
                                    ))}
                                  </List>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <>
                            {activeSubCategory.length > 0 && (
                              <div className="w-full border-r border-yellow-400">
                                {activeSubCategory.map((item, index) => (
                                  <div
                                    key={index}
                                    className={clsx(
                                      "w-full max-w-[461px] px-8 pb-10 xl:px-10",
                                      index !== 0 &&
                                        "border-t border-yellow-400 pt-8",
                                    )}
                                  >
                                    <label
                                      htmlFor=""
                                      className="font-bricolage-grotesque text-base font-normal text-primary-900"
                                    >
                                      {item.heading}
                                    </label>
                                    <List className="flex flex-col gap-y-2 pl-5 pt-5">
                                      {item.items.map((itm, idx) => (
                                        <MenuItem
                                          key={idx}
                                          component={Link}
                                          href={itm.href}
                                          className="w-fit bg-white py-2 font-bricolage-grotesque text-base font-light text-black-900 hover:text-secondary-900"
                                        >
                                          {itm.label}
                                        </MenuItem>
                                      ))}
                                    </List>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ))}

                      {/* Subcategory: right section */}
                      {activeSubCategory && (
                        <div className="h-full w-full max-w-[461px] pl-5">
                          {activeSubCategory.length > 1 &&
                            activeSubCategory
                              .slice(midValue(activeSubCategory.length))
                              .map((item, index) => (
                                <div
                                  key={index}
                                  className={clsx(
                                    "pb-[35px]",
                                    index !== 0 &&
                                      "border-t border-yellow-400 pt-7",
                                  )}
                                >
                                  <span className="block font-bricolage-grotesque text-base font-normal text-primary-900">
                                    {item.heading}
                                  </span>
                                  <List className="flex flex-col gap-y-2 pl-5 pt-5 text-black-900">
                                    {item.items.map((itm, idx) => (
                                      <MenuItem
                                        key={idx}
                                        component={Link}
                                        href={itm.href}
                                        className="w-fit bg-white py-[6px] font-bricolage-grotesque text-base font-light leading-[22px] text-black-900 hover:text-secondary-900"
                                      >
                                        {itm.label}
                                      </MenuItem>
                                    ))}
                                  </List>
                                </div>
                              ))}

                          {/* Left side section: image and button */}
                          {activeSubCategory.length < 2 && (
                            <MenuDownloadSection
                              heading={
                                categoryWithSubCategory[activeCategory]
                                  .downloadBook?.heading
                              }
                              image={
                                categoryWithSubCategory[activeCategory]
                                  .downloadBook?.image
                              }
                              button={
                                categoryWithSubCategory[activeCategory]
                                  .downloadBook?.button
                              }
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </Menu.Dropdown>
        </Menu>
      )}
    </div>
  );
};

export default MegaMenuDropdown;
